import { Client, type IFrame } from "@stomp/stompjs";
import { API_BASE_URL } from "@/config";

/**
 * Factory + lifecycle for the shared realtime socket. Wraps {@link Client}
 * from {@code @stomp/stompjs} so callers don't import STOMP internals.
 *
 * <p>One WebSocket carries two lanes on separate user queues:
 * {@code /user/queue/notifications} (bell/inbox) and
 * {@code /user/queue/resource-events} (silent cache coherence). Both are
 * subscribed on connect.
 *
 * <p>Heartbeats are 25s/25s — must match {@code WebSocketConfiguration}
 * on the backend. The browser sends the SESSION cookie automatically on
 * the WebSocket upgrade so no bearer token plumbing is needed; auth
 * follows the same model as REST.
 *
 * <p>Reconnect is exponential with a 30s cap. On every transition to
 * {@code open} the provider component runs its connect-driven gap-fill
 * (inbox + loaded project documents) — the only "polling-shaped" calls in
 * the system, and they're connect-driven, not timers.
 */
export interface RealtimeSocket {
  client: Client;
  activate(): void;
  deactivate(): Promise<void>;
}

export interface RealtimeSocketOptions {
  onNotification: (raw: string) => void;
  onResourceEvent?: (raw: string) => void;
  onState: (state: "connecting" | "open" | "closed") => void;
}

export function createRealtimeSocket(opts: RealtimeSocketOptions): RealtimeSocket {
  // Convert https:// or http:// to wss:// or ws:// for the WebSocket scheme.
  const wsBase = API_BASE_URL.replace(/^http(s?):\/\//, (_match, secure) => (secure ? "wss://" : "ws://"));
  const brokerURL = `${wsBase}/cns/v1/ws`;

  const client = new Client({
    brokerURL,
    // Initial reconnect delay; updated below to apply exponential backoff per failure.
    reconnectDelay: 2_000,
    heartbeatIncoming: 25_000,
    heartbeatOutgoing: 25_000,
    debug:
      process.env.NODE_ENV === "development"
        ? (msg) => {
            // Avoid spamming the console with heartbeat probes.
            if (!msg.includes("\n>>> PING") && !msg.includes("\n<<< PONG")) {
              // eslint-disable-next-line no-console
              console.debug("[stomp]", msg);
            }
          }
        : () => {},
  });

  let attempt = 0;

  client.onConnect = (_frame: IFrame) => {
    attempt = 0;
    opts.onState("open");
    client.subscribe("/user/queue/notifications", (msg) => opts.onNotification(msg.body));
    if (opts.onResourceEvent) {
      client.subscribe("/user/queue/resource-events", (msg) => opts.onResourceEvent!(msg.body));
    }
  };

  client.onWebSocketClose = () => {
    attempt += 1;
    // 2s, 4s, 8s, 16s, 30s cap. Cap exists because the SESSION cookie has a 3h
    // server timeout; we never want to back off so far that we miss the rest of it.
    client.reconnectDelay = Math.min(30_000, 2_000 * Math.pow(2, Math.min(attempt - 1, 4)));
    opts.onState("closed");
  };

  client.onStompError = (frame) => {
    // Broker-rejected frames (SecurityException from the inbound channel
    // interceptor) land here. Not recoverable without re-auth, so flip to
    // closed and let the auth-context drive the next move.
    // eslint-disable-next-line no-console
    console.error("[stomp] broker error", frame.headers, frame.body);
    opts.onState("closed");
  };

  return {
    client,
    activate: () => {
      opts.onState("connecting");
      client.activate();
    },
    deactivate: () => client.deactivate(),
  };
}
